import { useState } from 'react';
import { Box, Button, Flex, Group, Paper, Text } from '@mantine/core';
import { IconUserPlus } from '@tabler/icons-react';
import { UnrecognisedAttendees } from 'src/meetings/detail/components/UnrecognisedAttendees';
import { AddExistingClientModal } from 'src/meetings/detail/components/AddExistingClientModal';
import type { CalendarEvent } from 'src/meetings/models';
import type { MeetingScope } from 'src/meetings/scope';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';

type Props = {
  event: CalendarEvent;
  scope: MeetingScope;
  onLinked: () => Promise<void> | void;
  // 'recorded' is a past meeting that already has a recording; 'upcoming' is a
  // meeting that has not happened yet. Only the intro copy differs.
  context?: 'recorded' | 'upcoming';
};

const INTRO_COPY: Record<NonNullable<Props['context']>, string> = {
  recorded:
    "We've recorded this meeting, but a client needs to be linked in order to generate summaries, meeting notes, transcripts, and compliance reports. Link an existing client or create a new one from the attendees below.",
  upcoming:
    'This meeting is not linked to a client yet. Link one so its summary, meeting notes, transcript, and compliance report are saved to their profile once it is recorded. Link an existing client or create a new one from the attendees below.',
};

// Shown on the practice-wide hub for a meeting that has no client linked yet:
// summaries, notes and transcripts can only be produced once a client is
// attached, so we offer to create or link one from the attendees.
export function NonClientMeetingLink({ event, scope, onLinked, context = 'recorded' }: Props) {
  const [showAddExistingClientModal, setShowAddExistingClientModal] = useState(false);

  const handleChanged = async () => {
    await onLinked();
  };

  return (
    <Box mt="xl">
      <Paper px="md" py="md" bg="gray.0" maw={800} style={{ borderRadius: 12 }}>
        <Flex direction="column" gap="xs">
          <Text fw={FONT_WEIGHTS.semibold}>No client linked to this meeting</Text>
          <Text>{INTRO_COPY[context]}</Text>
        </Flex>
      </Paper>

      <Box mt="xl">
        <UnrecognisedAttendees event={event} scope={scope} onChanged={handleChanged} />
      </Box>

      <Group gap="md" mt="md" maw={800}>
        <Button
          variant="secondary"
          leftSection={<IconUserPlus size={16} />}
          onClick={() => setShowAddExistingClientModal(true)}
        >
          Add existing client
        </Button>
        <Text size="sm" c="gray.6">
          Link a client that&apos;s already in Afternoon to this meeting
        </Text>
      </Group>

      <AddExistingClientModal
        opened={showAddExistingClientModal}
        onClose={() => setShowAddExistingClientModal(false)}
        eventId={event.id}
        onClientAdded={handleChanged}
      />
    </Box>
  );
}
